import React from "react";
import { Button } from "../../components/OldFront/Simple/ButtonComponent/Button";
import Page from "../../components/PageComponent/Page";
import Heading4 from "../../components/OldFront/Simple/Heading4Component/Heading4";
import Heading5 from "../../components/OldFront/Simple/Heading5Component/Heading5";
import Heading6 from "../../components/OldFront/Simple/Heading6Component/Heading6";
import ContentContainer from "../../components/OldFront/Simple/ContentContainerComponent/ContentContainer";
import Map from "../../components/OldFront/Complex/MapComponent/Map";
import Amenities from "../../components/OldFront/Complex/AmenitiesComponent/Amenities";
import SafeHtmlRender from "../../components/SafeHtmlRenderComponent/SafeHtmlRender";
import Documents from "../../components/DocumentsComponent/Documents";
import Card from "../../components/OldFront/Simple/CardComponent/Card";
import * as DisplayAddress from "../../../../domain/models/DisplayAddress";
import * as MapAddress from "../../../../domain/models/MapAddress";
import * as FirstPartyFetchResponse from "../../../../domain/models/FirstPartyFetchResponse";
import { pipe } from "fp-ts/lib/function";
import { array } from "fp-ts/lib";
import { ImageGallery } from "../../components/ImageGalleryComponent/ImageGallery";
import { IImages } from "../../components/ImageGalleryComponent/imageGalleryImages";
import AnchorButton from "../../components/OldFront/Simple/AnchorButton/AnchorButton";
import * as ListingImage1 from "../../../../domain/models/ListingImage1";
import * as TCurrency from "../../../../domain/models/TCurrency";
import * as BookViewingModal from "../../containers/ListingContainer/BookViewingModal";
import * as BookViewingModalAdditionalQuestions from "../../containers/ListingContainer/BookViewingModalAdditionalQuestions";
import * as BookViewingModalSuccess from "../../containers/ListingContainer/BookViewingModalSuccess";
import { FrontHeader } from "../../components/Front/Simple/FrontHeader/FrontHeader";
import { FrontPill } from "../../components/Front/Simple/FrontPill/FrontPill";
import { FrontInfoBubble } from "../../components/Front/Simple/FrontInfoBubble/FrontInfoBubble";
import { listingKeyFacts, ListingKeyFacts } from "../../components/Front/Complex/ListingKeyFacts/ListingKeyFacts";
import { hasAnythingToShow, ListingInvestorBlock } from "../../components/Front/Complex/ListingInvestorBlock/ListingInvestorBlock";
import { listingGalleryImages } from "../../../../domain/listing/galleryImages";
import { ListingInfoRequestForm } from "../../components/Front/Complex/ListingInfoRequestForm/ListingInfoRequestForm";
import { WeightSemiBold } from "../../components/WeightSemiBold/WeightSemiBold";
import { FrontColors } from "../../models/FrontColors";
import * as Listing6 from "../../../../domain/models/Listing6";
import { requireExhaustive } from "../../../../shared/src/util";
import * as TContainerStateProps from "../../state/TContainerStateProps";
import { createChangeRouteAction } from "../../state/router/createChangeRouteAction";
import { THexColor } from "../../models/StringLiterals";

const renderMarketingStatePill = (marketingState: Listing6.TMarketingState): JSX.Element | undefined => {
    switch (marketingState) {
        case "coming_soon":
            return <FrontPill
                label="Coming soon"
                backgroundColor={FrontColors.PERTINENT_CORAL_21}
                textColor={FrontColors.NEUTRAL_INK}
            />;
        case "archived":
            return <FrontPill
                label="No longer available"
                backgroundColor={FrontColors.NEUTRAL_10_2}
                textColor={FrontColors.NEUTRAL_INK}
            />;
        case "live":
            return undefined;
        default:
            return requireExhaustive(marketingState);
    }
};

const renderMarketingStateNote = (marketingState: Listing6.TMarketingState): JSX.Element | undefined => {
    switch (marketingState) {
        case "coming_soon":
            return <div className="listing__state-note">
                <FrontInfoBubble color={FrontColors.NEUTRAL_12}>
                    <WeightSemiBold>Not on the market yet</WeightSemiBold>
                    <div className="listing__state-note-text">
                        We are still preparing this home, so the photos and details may change. Book a viewing to see it first.
                    </div>
                </FrontInfoBubble>
            </div>;
        case "archived":
            return <div className="listing__state-note">
                <FrontInfoBubble color={FrontColors.NEUTRAL_10_2}>
                    <WeightSemiBold>No longer on the market</WeightSemiBold>
                    <div className="listing__state-note-text">
                        The details below are the ones this home was last advertised with.
                    </div>
                </FrontInfoBubble>
            </div>;
        case "live":
            return undefined;
        default:
            return requireExhaustive(marketingState);
    }
};

type TPriceBadge = {
    label: string;
    backgroundColor: THexColor;
    textColor: THexColor;
};

const saleStateBadge = (saleState: Listing6.TSaleState): TPriceBadge | undefined => {
    switch (saleState) {
        case "for_sale":
            return {
                label: "For sale",
                backgroundColor: FrontColors.POSITIVE_APPLE_22,
                textColor: FrontColors.POSITIVE_APPLE_2,
            };
        case "under_offer":
            return {
                label: "Under offer",
                backgroundColor: FrontColors.PERTINENT_CORAL_22,
                textColor: FrontColors.PERTINENT_CORAL_2,
            };
        case "for_auction":
            return {
                label: "For auction",
                backgroundColor: FrontColors.PRIMARY_16,
                textColor: FrontColors.PRIMARY_2,
            };
        case "off_market":
            return undefined;
        default:
            return requireExhaustive(saleState);
    }
};

const priceBadges = (listing: Listing6.T): Array<TPriceBadge> =>
    pipe(
        [
            saleStateBadge(listing.sale_state),
            listing.is_probate
                ? {
                    label: "Probate",
                    backgroundColor: FrontColors.PRIMARY_22,
                    textColor: FrontColors.PRIMARY_2,
                }
                : undefined,
            listing.investor_figures.has_strong_rental_return
                ? {
                    label: "Good rent for the price",
                    backgroundColor: FrontColors.POSITIVE_APPLE_22,
                    textColor: FrontColors.POSITIVE_APPLE_0,
                }
                : undefined,
        ],
        array.filter((badge): badge is TPriceBadge => badge !== undefined),
    );

interface IListingPageState {
    bookingWidgetVisible: boolean;
    infoRequestOpen: boolean;
}

export interface IListingPageProps extends TContainerStateProps.TContainerStateProps {
    bookViewingModalOpen: boolean;
}

class ListingPage extends React.Component<React.PropsWithChildren<IListingPageProps>, IListingPageState> {

    constructor(props: IListingPageProps) {
        super(props);
        this.state = {
            bookingWidgetVisible: false,
            infoRequestOpen: false,
        };
    }

    public componentDidUpdate(): void {
        if (this.props.state.activeData.listing.listingResponse.tag !== "2XX") {
            document.title = "Properties for sale | Sail Homes";
            return;
        }

        const listing = this.props.state.activeData.listing.listingResponse.response.data;
        const address = pipe(DisplayAddress.fromListing6(listing), DisplayAddress.toString);

        document.title = listing.marketing_state === "archived"
            ? `${address} | Sail Homes`
            : listing.marketing_state === "coming_soon"
                ? `Coming soon: ${address} | Sail Homes`
                : `For sale: ${address} | Sail Homes`;
    }

    public render (): JSX.Element {
        if (this.props.state.activeData.listing.listingResponse.tag !== FirstPartyFetchResponse.constants.STATUS_2XX) {
            return <div></div>;
        }

        const listing = this.props.state.activeData.listing.listingResponse.response.data;
        const badges = priceBadges(listing);
        const infoRequest = this.props.state.activeData.listingInfoRequest;
        const galleryImages = listingGalleryImages(
            listing.images,
            listing.documents,
            env.REACT_APP_ACCOUNT_NAME || "",
        );
        const keyFactsProps = {
            propertyType: listing.property_type,
            bedroomCount: listing.bedroom_count,
            bathroomCount: listing.bathroom_count,
            receptionRoomCount: listing.reception_room_count,
            floorArea: listing.approximate_floor_area,
            floorAreaUnits: listing.approximate_floor_area_units,
        };
        const hasSidebarFacts =
            listingKeyFacts(keyFactsProps).length > 0
            || hasAnythingToShow(listing.investor_figures);
        const tenureLabel =
            listing.tenure === "freehold" ? "Freehold Property"
            : listing.tenure === "leasehold" ? "Leasehold Property"
            : "";

        return (
            <Page
                showLogout={false}
            >
                {/* MODAL: BOOK VIEWING  */}
                {this.props.bookViewingModalOpen &&
                    <div className="listing__book-viewing-modal-container">
                        {this.props.state.routes.active === "VIEW_LISTING_BOOK_VIEWING" &&
                            BookViewingModal.render(this.props, listing)
                        }
                        {this.props.state.routes.active === "VIEW_LISTING_BOOK_VIEWING_ADDITIONAL_QUESTIONS" &&
                            BookViewingModalAdditionalQuestions.render(this.props, listing)
                        }
                        {this.props.state.routes.active === "VIEW_LISTING_BOOK_VIEWING_SUCCESS" &&
                            BookViewingModalSuccess.render(this.props, listing)
                        }
                    </div>
                }

                <div className={
                        "listing__page-section" +
                        (this.props.bookViewingModalOpen ? " listing__page-section--hidden" : "")
                    }
                    style={this.props.bookViewingModalOpen
                        ? {
                            top: `-${window.scrollY}"px"`,
                        } : {
                            top: "",
                        }
                    }
                >
                    <FrontHeader
                        backLink={{ label: "Back to all properties", url: "/listings" }}
                        pill={renderMarketingStatePill(listing.marketing_state)}
                        width="content"
                        titleHeading="h1"
                        meta={
                            <>
                                {/* A listing being prepared often has no price yet. The old
                                    fallback rendered it as £0, which is worse than silence —
                                    especially on a page the agent shares with the seller. */}
                                {listing.price !== null &&
                                    <span className="listing__hero-price">
                                        {TCurrency.fromNumber(listing.price)}
                                    </span>
                                }
                                {tenureLabel !== "" &&
                                    <span className="listing__hero-tenure">{tenureLabel}</span>
                                }
                                {badges.length > 0 &&
                                    <div className="listing__price-badges">
                                        {badges.map((badge) => (
                                            <FrontPill
                                                key={badge.label}
                                                label={badge.label}
                                                backgroundColor={badge.backgroundColor}
                                                textColor={badge.textColor}
                                            />
                                        ))}
                                    </div>
                                }
                            </>
                        }
                    >
                        {pipe(
                            DisplayAddress.fromListing6(listing),
                            DisplayAddress.toShortString,
                        )}
                    </FrontHeader>
                    <div className="listing__top-grid-section">
                        <ContentContainer className="listing__top-grid-container">
                            {galleryImages.length > 0 ? (
                                <ContentContainer className="listing__images-container">
                                    <div className="listing__image-gallery-container-aspect-ratio-setter">
                                        <div className="listing__image-gallery-container-position-setter">
                                            <ImageGallery
                                                images={array.map<ListingImage1.T, IImages>((image) => ({
                                                    src: image.url,
                                                    alt: image.name,
                                                }))(galleryImages)}
                                                showCarousel={true}
                                            />
                                        </div>
                                    </div>
                                </ContentContainer>
                            ) : listing.marketing_state === "coming_soon" ? (
                                <ContentContainer className="listing__images-container">
                                    <div className="listing__images-placeholder">
                                        <Heading5 text="Photography coming soon" />
                                        <div className="listing__images-placeholder-text">
                                            We add photos before a home goes on the market.
                                        </div>
                                    </div>
                                </ContentContainer>
                            ) : ""}
                            <ContentContainer className="listing__sidebar-container">
                                {renderMarketingStateNote(listing.marketing_state)}
                                {/* On mobile the sticky bar carries these same two actions, so
                                    this pair is hidden there. An archived listing has no sticky
                                    bar, and its single CTA stays at every width. */}
                                <div className={
                                    "listing__cta" +
                                    (listing.marketing_state !== "archived" ? " listing__cta--repeated-by-sticky-bar" : "")
                                }>
                                    {listing.marketing_state === "archived" ? (
                                        <AnchorButton
                                            label={`See homes for sale in ${listing.city_town}`}
                                            type="rooster"
                                            disabled={false}
                                            url={`/listings?q=${encodeURIComponent(listing.city_town)}`}
                                            fullWidth={true}
                                        />
                                    ) : (
                                        <>
                                            <Button
                                                label="Book a viewing"
                                                type="rooster"
                                                disabled={false}
                                                onClick={() => this.props.dispatch(createChangeRouteAction("VIEW_LISTING_BOOK_VIEWING", { listingId: this.props.state.routes.params.listingId }, {}))}
                                                fullWidth={true}
                                            />
                                            <ListingInfoRequestForm
                                                address={pipe(DisplayAddress.fromListing6(listing), DisplayAddress.toShortString)}
                                                onClose={() => this.setState({ infoRequestOpen: false })}
                                                firstName={infoRequest.firstName}
                                                lastName={infoRequest.lastName}
                                                contactEmail={infoRequest.contactEmail}
                                                contactPhoneNumber={infoRequest.contactPhoneNumber}
                                                submitting={infoRequest.submitting}
                                                submitted={infoRequest.submitted && infoRequest.forListingId === listing.id}
                                                failed={infoRequest.failed && infoRequest.forListingId === listing.id}
                                                isOpen={this.state.infoRequestOpen}
                                                onOpen={() => this.setState({ infoRequestOpen: true })}
                                                onFirstNameChange={(v) => this.props.dispatch({ type: "LISTING_INFO_REQUEST_FIRST_NAME_CHANGED", payload: v })}
                                                onLastNameChange={(v) => this.props.dispatch({ type: "LISTING_INFO_REQUEST_LAST_NAME_CHANGED", payload: v })}
                                                onEmailChange={(v) => this.props.dispatch({ type: "LISTING_INFO_REQUEST_EMAIL_CHANGED", payload: v })}
                                                onPhoneChange={(v) => this.props.dispatch({ type: "LISTING_INFO_REQUEST_PHONE_CHANGED", payload: v })}
                                                onSubmit={() => this.props.dispatch({ type: "LISTING_INFO_REQUEST_SUBMITTED", payload: listing.id })}
                                            />
                                        </>
                                    )}
                                </div>
                                {/* The wrapper owns the gap below the buttons, so it has to go
                                    too when both panels withhold — otherwise a listing with
                                    nothing to show still pays for the space. */}
                                {listing.marketing_state !== "archived" && hasSidebarFacts &&
                                    <div className="listing__sidebar-facts">
                                        <ListingKeyFacts {...keyFactsProps} />
                                        <ListingInvestorBlock figures={listing.investor_figures} />
                                    </div>
                                }
                            </ContentContainer>
                        </ContentContainer>
                    </div>
                    <div className="listing__body-section">
                        <ContentContainer className="listing__body-container">
                            <Card>
                                <div>
                                    <div className="listing__detail-container">
                                        {/* Description */}
                                        <Heading4 text="Description"/>
                                        <div className="listing__detail-text-container">
                                            <SafeHtmlRender html={listing.description}/>
                                        </div>
                                    </div>
                                    {listing.documents.length > 0 ? (
                                        <div className="listing__detail-container">
                                            {/* Documents */}
                                            <Heading4 text="Documents"/>
                                            <Documents
                                                data={listing.documents}
                                                address={DisplayAddress.toBuildingAndStreet(DisplayAddress.fromListing6(listing))}
                                            />
                                        </div>
                                    ) : ""}
                                    {listing.amenities.length > 0 ? (
                                        <div className="listing__detail-container">
                                            {/* Amenities */}
                                            <Heading4 text="Nearby Amenities" />
                                            <Amenities
                                                data={listing.amenities}
                                                address={DisplayAddress.toBuildingAndStreet(DisplayAddress.fromListing6(listing))}
                                            />
                                        </div>
                                    ) : ""}
                                </div>
                            </Card>
                        </ContentContainer>
                    </div>
                    <div className="listing__map-section">
                        <ContentContainer className="listing__map-container">
                            <div className="listing__map-heading">
                                <Heading4 text="Property Location" />
                            </div>
                            <div className="listing__map-frame">
                                <Map
                                    address={MapAddress.fromListing6(listing)}
                                    latLng={
                                        listing.latitude
                                        && listing.longitude
                                        ? [
                                            listing.latitude,
                                            listing.longitude,
                                        ] :
                                        undefined
                                    }
                                />
                            </div>
                        </ContentContainer>
                    </div>
                    {listing.marketing_state !== "archived" &&
                    <div className="listing__cta2-section listing__cta2-section--wide-only">
                        <ContentContainer className="listing__cta2-container">
                            {/* CTA 2 */}
                            <div className="listing__cta2-words">
                                <div className="listing__cta2-main-heading-container">
                                    <div className="listing__cta2-heading-container">
                                        <Heading5 text="Love this property?" />
                                    </div>
                                    <div className="listing__cta2-heading-container">
                                        <Heading5 text="Book a viewing or make an offer" />
                                    </div>
                                </div>
                                <div className="listing__cta2-heading-container">
                                    <Heading6 text="We recommend viewing before making an offer. Get in there!"/>
                                </div>
                            </div>
                            <div className="listing__cta2-buttons">
                                <div className="listing__cta2-buttons-single-container">
                                    <Button
                                        label="Book a viewing"
                                        type="rooster"
                                        disabled={false}
                                        onClick={() => this.props.dispatch(createChangeRouteAction("VIEW_LISTING_BOOK_VIEWING", { listingId: this.props.state.routes.params.listingId }, {}))}
                                        fullWidth={true}
                                    />
                                </div>
                            </div>
                        </ContentContainer>
                    </div>
                    }
                    {listing.marketing_state !== "archived" &&
                        <div className="listing__sticky-actions">
                            <Button
                                label="Book a viewing"
                                type="rooster"
                                disabled={false}
                                onClick={() => this.props.dispatch(createChangeRouteAction("VIEW_LISTING_BOOK_VIEWING", { listingId: this.props.state.routes.params.listingId }, {}))}
                                fullWidth={true}
                            />
                            <Button
                                label="Request more info"
                                type="otter"
                                disabled={false}
                                onClick={() => this.setState({ infoRequestOpen: true })}
                                fullWidth={true}
                            />
                        </div>
                    }
                </div>
            </Page>
        );
    }
}

export default ListingPage;
